import React from 'react';
import '../components/grammar-page.css';
import { FaBookOpen, FaLightbulb } from 'react-icons/fa';

const SomeanyExplanation = () => {
  return (
    <div className="grammar-explanation">
      <h2><FaBookOpen /> Countable & Uncountable Nouns — plus <em>some</em> and <em>any</em></h2>

      <div className="example-block">
        <h3><FaLightbulb /> 1. Countable Nouns</h3>
        <p><strong>Countable nouns</strong> are items you can count one by one. They have singular and plural forms.</p>
        <ul>
          <li>Singular: a or an + noun — e.g. <strong>a book</strong>, <strong>an apple</strong>.</li>
          <li>Plural: use the plural form — e.g. <strong>books</strong>, <strong>apples</strong>.</li>
        </ul>
        <p><strong>Examples:</strong> one chair, two chairs; a dog, three dogs.</p>
      </div>

      <div className="example-block">
        <h3><FaLightbulb /> 2. Uncountable Nouns</h3>
        <p><strong>Uncountable nouns</strong> (also called mass nouns) are things you cannot count as individual items. They usually have only one form and do not use a/an.</p>
        <ul>
          <li>Examples: <strong>water</strong>, <strong>rice</strong>, <strong>advice</strong>, <strong>music</strong>, <strong>information</strong>.</li>
          <li>To talk about quantity, use words like some, a little, much, or measure words: a glass of water, a piece of advice.</li>
        </ul>
      </div>

      <div className="example-block">
        <h3><FaLightbulb /> 3. Using <em>some</em> and <em>any</em></h3>
        <p><strong>some</strong> and <strong>any</strong> are used with both countable (plural) and uncountable nouns when we talk about an indefinite amount or number.</p>

        <h4>When to use <em>some</em></h4>
        <ul>
          <li>In <strong>positive (affirmative)</strong> sentences:
            <br />There are <strong>some</strong> apples.
            <br />I have <strong>some</strong> money.</li>
          <li>In <strong>offers</strong> and <strong>requests</strong> (questions) when you expect the answer to be "yes":
            <br />Would you like <strong>some</strong> tea?
            <br />Can I have <strong>some</strong> sugar?</li>
        </ul>

        <h4>When to use <em>any</em></h4>
        <ul>
          <li>In <strong>negative</strong> sentences:
            <br />I don't have <strong>any</strong> money.</li>
          <li>In <strong>questions</strong> when you are not expecting a particular answer:
            <br />Do you have <strong>any</strong> brothers or sisters?</li>
          <li>In <strong>conditional</strong> sentences:
            <br />If you need <strong>any</strong> help, call me.</li>
        </ul>

        <h4>Quick Summary</h4>
        <ul>
          <li><strong>some</strong> → positive statements; offers/requests (when expecting yes).</li>
          <li><strong>any</strong> → negatives, most questions, and conditionals.</li>
          <li>Both words work with plural countable nouns (<em>some apples / any apples</em>) and uncountable nouns (<em>some water / any water</em>).</li>
        </ul>
      </div>

      <div className="example-block">
        <h3><FaLightbulb /> 4. Short Examples</h3>
        <ul>
          <li>Countable: I have <strong>some</strong> friends. / Do you have <strong>any</strong> friends in Paris?</li>
          <li>Uncountable: There is <strong>some</strong> milk in the fridge. / There isn't <strong>any</strong> milk left.</li>
          <li>Offer: Would you like <strong>some</strong> coffee?</li>
          <li>Question (neutral): Is there <strong>any</strong> sugar?</li>
        </ul>
      </div>

      <div className="example-block">
        <h3><FaLightbulb /> 5. Mini Practice</h3>
        <ol>
          <li>_____ water in the bottle. (affirmative)</li>
          <li>Do you have _____ questions? (neutral question)</li>
          <li>I don't have _____ idea. (negative)</li>
          <li>Would you like _____ cake? (offer)</li>
        </ol>
        <details>
          <summary>Answers</summary>
          <ol>
            <li><strong>some</strong> water in the bottle.</li>
            <li>Do you have <strong>any</strong> questions?</li>
            <li>I don't have <strong>any</strong> idea.</li>
            <li>Would you like <strong>some</strong> cake?</li>
          </ol>
        </details>
        <p><em>Tip: if you can count the thing (one, two, three...), it's usually countable. If not (liquid, abstract, powder, information), it's often uncountable.</em></p>
      </div>
    </div>
  );
};

export default SomeanyExplanation;
